import sectionRequirementsJson from 'main/data/med-school/sectionRequirements.json';

import { COPY, schoolProgramGroup, SCHOOLS } from './data';
import { interpolate } from './interpolate';
import {
  type Country,
  type PartialSectionScores,
  type ProgramGroupKey,
  type School,
  SECTION_KEYS,
  type SectionKey,
} from './types';

export const CLASSIFY_THRESHOLDS = {
  gpaWeight: 10,
  inRegionShare: 70,
  likelyMinIndex: 1,
  mcatDivisor: 2,
  reachMaxIndex: -2,
} as const;

export type ClassifyBucket = 'likely' | 'reach' | 'target';

export const CLASSIFY_BUCKETS: readonly ClassifyBucket[] = ['likely', 'target', 'reach'];

export type ClassifyInput = Readonly<{
  country?: Country;
  gpa?: number;
  mcat?: number;
  programGroups?: readonly ProgramGroupKey[];
  region?: string;
  sections?: PartialSectionScores;
}>;

export type MinimumFlag = Readonly<{
  kind: 'cars_minimum' | 'mcat_minimum' | 'section_minimum';
  minimum: number;
  reason: string;
  section?: SectionKey;
}>;

export type ClassifiedSchool = Readonly<{
  bucket: ClassifyBucket;
  demoted: boolean;
  flags: readonly MinimumFlag[];
  index: number;
  school: School;
}>;

export type ClassifyResult = Readonly<{
  likely: readonly ClassifiedSchool[];
  reach: readonly ClassifiedSchool[];
  target: readonly ClassifiedSchool[];
  unrated: readonly School[];
}>;

export function comparisonIndex(input: ClassifyInput, school: School): number | null {
  const { gpa, mcat } = input;
  let index = 0;
  let components = 0;

  if (gpa !== undefined && school.median_gpa !== null) {
    index += (gpa - school.median_gpa) * CLASSIFY_THRESHOLDS.gpaWeight;
    components += 1;
  }
  if (mcat !== undefined && school.median_mcat !== null) {
    index += (mcat - school.median_mcat) / CLASSIFY_THRESHOLDS.mcatDivisor;
    components += 1;
  }

  return components === 0 ? null : index;
}

export function bucketForIndex(index: number): ClassifyBucket {
  if (index >= CLASSIFY_THRESHOLDS.likelyMinIndex) return 'likely';
  return index >= CLASSIFY_THRESHOLDS.reachMaxIndex ? 'target' : 'reach';
}

export function demoteBucket(bucket: ClassifyBucket): ClassifyBucket {
  if (bucket === 'likely') return 'target';
  return 'reach';
}

export function shouldDemote(input: ClassifyInput, school: School): boolean {
  const { country, region } = input;
  const outsideCountry = country !== undefined && country !== school.country;
  if (!outsideCountry && (region === undefined || region === '' || region === school.region)) return false;
  const share = school.in_region_share;
  return share !== null && share >= CLASSIFY_THRESHOLDS.inRegionShare;
}

type SectionRule = Readonly<{
  allowance_minimum: number | null;
  exclude_regions: readonly string[] | null;
  minimums: Readonly<Record<SectionKey, number | null>>;
  regions: readonly string[] | null;
  school_key: string;
}>;

export const SECTION_RULES: readonly SectionRule[] = sectionRequirementsJson.rules;

// Published per-section rules (sectionRequirements.json) for schools whose structured fields carry none, e.g.
// Toronto's 125 in each section. Province-limited rules only count once the person has picked a province, and a
// one-section allowance ("one 124 allowed") forgives a single section at or above its floor.
function publishedRuleMinimums(input: ClassifyInput, school: School): Map<SectionKey, number> {
  const minimums = new Map<SectionKey, number>();
  const { region, sections } = input;
  if (sections === undefined || school.section_minimums !== null || school.cars_minimum !== null) return minimums;

  for (const rule of SECTION_RULES) {
    if (rule.school_key !== school.key) continue;
    const limited = rule.regions !== null || rule.exclude_regions !== null;
    if (limited && (region === undefined || region === '')) continue;
    if (rule.regions !== null && !rule.regions.includes(region ?? '')) continue;
    if (rule.exclude_regions?.includes(region ?? '')) continue;

    const below = SECTION_KEYS.filter(key => {
      const value = sections[key];
      const minimum = rule.minimums[key];
      return value !== undefined && minimum !== null && value < minimum;
    });
    const [only] = below;
    const allowance = rule.allowance_minimum;
    const forgiven =
      allowance !== null && below.length === 1 && only !== undefined && (sections[only] ?? 0) >= allowance;
    if (forgiven) continue;
    for (const key of below) {
      const minimum = rule.minimums[key] ?? 0;
      minimums.set(key, Math.max(minimum, minimums.get(key) ?? 0));
    }
  }
  return minimums;
}

export function minimumFlags(input: ClassifyInput, school: School): readonly MinimumFlag[] {
  const { flags: flagCopy } = COPY.calculator;
  const { sections: sectionCopy } = COPY.section_feedback;
  const { mcat, sections } = input;
  const flags: MinimumFlag[] = [];

  if (mcat !== undefined && school.mcat_minimum !== null && mcat < school.mcat_minimum) {
    flags.push({
      kind: 'mcat_minimum',
      minimum: school.mcat_minimum,
      reason: interpolate(flagCopy.mcat_minimum, { minimum: school.mcat_minimum }),
    });
  }

  const cars = sections?.cars;
  const carsFlagged = cars !== undefined && school.cars_minimum !== null && cars < school.cars_minimum;
  if (carsFlagged) {
    flags.push({
      kind: 'cars_minimum',
      minimum: school.cars_minimum,
      reason: interpolate(flagCopy.cars_minimum, { minimum: school.cars_minimum }),
    });
  }

  const sectionMinimums = school.section_minimums;
  if (sections !== undefined && sectionMinimums !== null) {
    for (const key of SECTION_KEYS) {
      const value = sections[key];
      const minimum = sectionMinimums[key];
      if (value === undefined || minimum === null || value >= minimum) continue;
      if (key === 'cars' && carsFlagged) continue;
      flags.push({
        kind: 'section_minimum',
        minimum,
        reason: interpolate(flagCopy.section_minimum, { minimum, section_short: sectionCopy[key].short }),
        section: key,
      });
    }
  }

  for (const [key, minimum] of publishedRuleMinimums(input, school)) {
    flags.push({
      kind: 'section_minimum',
      minimum,
      reason: interpolate(flagCopy.section_minimum, { minimum, section_short: sectionCopy[key].short }),
      section: key,
    });
  }

  return flags;
}

// The program-type filter the results use. The GPA x MCAT chart plots the same set.
export function programSchools(
  programGroups: readonly ProgramGroupKey[] | undefined,
  schools: readonly School[] = SCHOOLS,
): readonly School[] {
  if (programGroups === undefined || programGroups.length === 0) return schools;
  return schools.filter(school => programGroups.includes(schoolProgramGroup(school)));
}

export function classifySchools(input: ClassifyInput, schools: readonly School[] = SCHOOLS): ClassifyResult {
  const buckets: Record<ClassifyBucket, ClassifiedSchool[]> = { likely: [], reach: [], target: [] };
  const unrated: School[] = [];

  for (const school of programSchools(input.programGroups, schools)) {
    const index = comparisonIndex(input, school);
    if (index === null) {
      unrated.push(school);
      continue;
    }

    const flags = minimumFlags(input, school);
    const demoted = shouldDemote(input, school);
    const rated = bucketForIndex(index);
    const bucket = demoted ? demoteBucket(rated) : rated;

    buckets[bucket].push({ bucket, demoted, flags, index, school });
  }

  for (const bucket of CLASSIFY_BUCKETS) buckets[bucket].sort((a, b) => b.index - a.index);
  unrated.sort((a, b) => a.name.localeCompare(b.name));

  return { likely: buckets.likely, reach: buckets.reach, target: buckets.target, unrated };
}
